import {makeAutoObservable} from "mobx";
import {IChangeable} from "../../../stores/types";
import ConditionValidator, { IConditionValidatorData} from "../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-validator";
import {CONDITION_GROUP_LEVELS, CONDITION_GROUP_OPERATOR, IPolicyCondition, IPolicyConditionGroup} from "../policy-types";
import ConditionBuilderStore from "./condition-builder-store";
import PolicyStore from "./policy-store";

interface IPolicyConditionGroupChanges extends Partial<IPolicyConditionGroup> {}

/** Components should ONLY call group's methods for editing conditions and never directly condition's methods  */
export class PolicyConditionGroupStore implements IChangeable<IPolicyConditionGroup> {
    private _policy: PolicyStore = {} as PolicyStore;
    private _model: IPolicyConditionGroup = {} as IPolicyConditionGroup;
    private _level: CONDITION_GROUP_LEVELS;
    private _changes: IPolicyConditionGroupChanges = {};
    private _uncommittedChanges: IPolicyConditionGroupChanges = {};
    private _policyConditions: ConditionBuilderStore[] = [];
    
    constructor(policy: PolicyStore, model: IPolicyConditionGroup, level: CONDITION_GROUP_LEVELS) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._policy = policy;
        this._model = ensureValidModelCopy(model);
        this._level = level;
        this._setConditionsModels();
    }

    private _setConditionsModels() {
        if (this._model.policy_rule_conditions) {
            this._policyConditions = this._model.policy_rule_conditions.map(policy_condition => new ConditionBuilderStore(this, policy_condition));
        }
    }

    public setChanges(changes: IPolicyConditionGroupChanges): void {
        delete changes.id;
        
        this._changes = changes;

        this._policyConditions = [];
        if (changes.policy_rule_conditions) {
            this._policyConditions = changes.policy_rule_conditions.map(policy_condition_changes => {
                const conditionStore = new ConditionBuilderStore(this, {} as IPolicyCondition);
                conditionStore.setChanges(policy_condition_changes);
                return conditionStore;
            });
        }
    }

    get policy() { return this._policy; }
    get level () { return this._level; }
    private get preset() { return this._policy.conditionPreset.getPresetByLevel(this._level).group; }

    get id(): number { return this._model.id; }
    get conditionsOperator(): CONDITION_GROUP_OPERATOR { return this._uncommittedChanges.conditions_operator ?? this._changes.conditions_operator ?? this._model.conditions_operator; }
    get isEligible(): boolean { return this._changes.is_eligible ?? this._model.is_eligible; }
    get policyConditions(): ConditionBuilderStore[] { return this._policyConditions.filter(c => !c.deleted); }
    get deleted() { return this._changes._deleted ?? false }

    public setDeleted(v: boolean) { 
        if (v) {
            this._changes._deleted = v; 
            return;
        }
        delete this._changes._deleted;
    }

    public get conditionsTitle():string {
        return this.preset.conditions_title;
    }

    public get isEditMode(): boolean {
        return this._policyConditions.some(c => c.isEditMode);
    }

    public alignConditions() {
        this._policyConditions.forEach(condition => condition.deleteIfNotAllowed());
    }

    public setConditionsOperator(value: CONDITION_GROUP_OPERATOR) {
        this._uncommittedChanges.conditions_operator = value;
    }

    public setIsEligible(value: boolean) {
        this._changes.is_eligible = value;
        this._clearUnneededChanges();    
    }

    public addCondition() {
        if (!this.tryCommitChanges()) {
            return;
        }

        const newCondition = new ConditionBuilderStore(this, {} as IPolicyCondition);
        newCondition.setIsEditMode(true);
        this._policyConditions.push(newCondition);
    }



    public setIsEditMode(condition: ConditionBuilderStore, isEditMode: boolean) {
        /** just in case, should never happen - validation that makes sure that this condition really exists in policy condition group */
        const index = this._policyConditions.indexOf(condition);
        if (index === -1) {
            new Error(`Condition could not be found in this group`);
        }

        if (!this.tryCommitChanges()) {
            return;
        }

        /** TODO: should we commit changes in prev edited condition or we should discard them? */
        // this._policyConditions.forEach(c => c.setIsEditMode(false));
        condition.setIsEditMode(isEditMode);
    }

    private _clearErrors() {
        this._policyConditions.forEach((c => c.setError(undefined)));
    }

    private _isValid(condition: ConditionBuilderStore): boolean {
        this._clearErrors();

        const conditionsWithSameField =  this._policyConditions.filter(c=> c !== condition && c.field === condition.field && !c.deleted);

        /** deep copy presets to avoid changing original */
        const preset = JSON.parse(JSON.stringify(this._policy.conditionPreset.getPresetByLevel(this._level)));
        const currentItem = condition.getUncommittedChanges();
        const itemsWithSameField = conditionsWithSameField.map(c => c.getUncommittedChanges());

        console.log('_isValid args', preset, currentItem, itemsWithSameField);
        
        const validator = new ConditionValidator(currentItem, itemsWithSameField, preset, this.conditionsOperator);
        const {conditionResponse: currentItemResponse, otherConditionsResponse: itemsWithSameFieldResponse} = validator.validate();
        
        let isThereError = false;
        if(currentItemResponse.error){
            isThereError = true;
            condition.setError(currentItemResponse.error)
        }
        itemsWithSameFieldResponse.forEach((otherConditionResponse: IConditionValidatorData, index) => {
            if (otherConditionResponse.error) {
                isThereError = true;
                conditionsWithSameField[index].setError(otherConditionResponse.error)
            }
        })

        return !isThereError;
    }

    public get eligibilityDescription(): string {
        return this.preset.eligibility_description;
    }

    public get eligibilityOptions() {
        return this.preset.eligibility;
    }

    public deleteCondition(condition: ConditionBuilderStore): void {
        condition.setDeleted(true);
        /** for conditions created and deleted on client - delete totally */
        this._removeGhostConditions();
        this._policy.finalizeConditionGroup(this._level);
    }

    public tryCommitChanges(condition?: ConditionBuilderStore): boolean {
        /** if there is other condition in edit mode - validate including uncommitted (tryCommitChanges) changes and prevent enter editing if not valid */
        condition = condition ?? this._policyConditions.find(c => c.isEditMode);
        /** if we are trying to commit changes when there is no condition to commit - do nothing */
        if (!condition) {
            return true;
        }
        
        if (!this._isValid(condition)) {            
            /** when not valid - we are not committing the changes */
            return false;
        }

        this._changes = { ...this._changes, ...this._uncommittedChanges };
        this._uncommittedChanges = {};

        condition.commitChanges();
        condition.setIsEditMode(false);
        this._clearUnneededChanges();
        return true;
    }

    public discardUncommittedChanges(condition?: ConditionBuilderStore) {
        condition = condition ?? this._policyConditions.find(c => c.isEditMode);
        /** when no condition in edit mode - nothing to commit */
        if (!condition) {
            return;
        }        

        condition.discardUncommittedChanges();
        condition.setIsEditMode(false);

        this._uncommittedChanges = {};
        this._removeGhostConditions();
        this._clearErrors();

        this._policy.finalizeConditionGroup(this._level);
    }

    public isEmpty(): boolean {
        /** when no visible conditions - consider empty, eg: deleted conditions are counted as invisible */
        return this.policyConditions.length === 0;
    }

    /** group considered ghost when it has no conditions and deleted and does not have model on server */
    public isGhost(): boolean {
        return this.isEmpty() && this.deleted && !this._model.id;
    }

    private _removeGhostConditions(): void {
        /** removes all conditions that are not in edit mode and have no information - ghosts */
        this._policyConditions = this._policyConditions.filter(c => !c.isGhost());
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IPolicyConditionGroup;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];    
            }
        });
    }

    hasChanges(): boolean {
        const hasConditionChanges = this._policyConditions?.some(c => c.hasChanges()) ?? false;
        return !!Object.keys(this._changes).length || hasConditionChanges;
    }

    discardChanges(): void {
        this._changes = {};
        this._setConditionsModels();        
    }

    getUpdatedModel(): IPolicyConditionGroup|undefined {
        // do not send if was created and then deleted on client
        const isNewAndDeleted = !(this._model ?? {}).id && this.deleted;
        if (isNewAndDeleted) {
            return undefined;
        }

        /** commented out _removeGhostConditions to NOT hide errors in condition handling, at this point (getUpdatedModel) the conditions should be perfect */
        // this._removeGhostConditions();
        const conditionsUpdatedModels = this._policyConditions.map(c => c.getUpdatedModel());

        return { 
            ...this._model, 
            ...this._changes,
            policy_rule_conditions: conditionsUpdatedModels as IPolicyCondition[],
            // we update on server the whole group, so enough that one of its conditions was changed
            _has_changes: this.hasChanges(), 
        };
    }
}

export default PolicyConditionGroupStore;

/** some fields receievd from an api has an incorrect type */
function ensureValidModelCopy(data: IPolicyConditionGroup): IPolicyConditionGroup {
    const toBool = (v: undefined | any) => Boolean(v);

    return {
        ...data,
        is_eligible: toBool(data.is_eligible),
    };
}